// SUMMARY: App launcher grid - the navbar's single index of every destination in the product. Regular
// destinations render with a BLUE icon, admin-only ones with a RED icon; the admin group is the sidebar's
// former ADMIN block, moved here so the sidebar stays short for everyone.

'use client'

import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { useSession } from 'next-auth/react'
import {
	BarChart3,
	BookOpen,
	Boxes,
	Brain,
	CreditCard,
	Database,
	Euro,
	FlaskConical,
	FolderKanban,
	Gauge,
	Gift,
	KeyRound,
	Library,
	MessageSquare,
	Terminal,
	Server as ServerIcon,
	ShieldAlert,
	Stamp,
	User,
	Workflow,
	type LucideIcon,
} from 'lucide-react'
import { DropdownMenuItem } from '@/components/ui/dropdown-menu'
import { PROJECTS_ENABLED } from '@/lib/features'
import { cn } from '@/lib/utils'

export type AppItem = {
	key: string
	href: string
	icon: LucideIcon
}

export type AppGridVariant = 'plain' | 'menu'

// Labels are reused from dashboard.sidebar.* so a rename can never leave the two navigations disagreeing.
export const APP_ITEMS: AppItem[] = [
	{ key: 'chat', href: '/dashboard/chat', icon: MessageSquare },
	...(PROJECTS_ENABLED ? [{ key: 'projects', href: '/dashboard/projects', icon: FolderKanban }] : []),
	{ key: 'memory', href: '/dashboard/settings/memory', icon: Brain },
	{ key: 'branding', href: '/dashboard/settings/branding', icon: Stamp },
	{ key: 'billing', href: '/dashboard/settings/billing', icon: CreditCard },
	{ key: 'account', href: '/dashboard/settings/account', icon: User },
]

// API access (CLI + keys) stays Standard/Pro-only until the public API launches. Eligibility is not in the
// session, so it is fetched from /api/nav-access the first time the menu is opened and passed in as showCli.
export const CLI_ITEMS: AppItem[] = [
	{ key: 'cli', href: '/dashboard/cli', icon: Terminal },
	{ key: 'apiKeys', href: '/dashboard/api-keys', icon: KeyRound },
]

export const ADMIN_ITEMS: AppItem[] = [
	{ key: 'freshChat', href: '/dashboard/chat/fresh', icon: FlaskConical },
	{ key: 'analytics', href: '/dashboard/admin/analytics', icon: BarChart3 },
	{ key: 'adminBilling', href: '/dashboard/admin/billing', icon: Euro },
	{ key: 'flow', href: '/dashboard/admin/flow', icon: Workflow },
	{ key: 'database', href: '/dashboard/admin/database', icon: Database },
	{ key: 'security', href: '/dashboard/admin/security', icon: ShieldAlert },
	{ key: 'access', href: '/dashboard/admin/access', icon: Gift },
	{ key: 'servers', href: '/dashboard/admin/servers', icon: ServerIcon },
	{ key: 'models', href: '/dashboard/admin/models', icon: Boxes },
	{ key: 'registry', href: '/dashboard/admin/registry', icon: Library },
	{ key: 'benchmark', href: '/dashboard/admin/benchmark', icon: Gauge },
	{ key: 'papers', href: '/dashboard/admin/papers', icon: BookOpen },
]

const TILE_CLASS =
	'flex flex-col items-center gap-1.5 rounded-lg px-2 py-3 text-center cursor-pointer transition-colors hover:bg-accent'

function AppTile({
	item,
	label,
	variant,
	tone,
	onNavigate,
}: {
	item: AppItem
	label: string
	variant: AppGridVariant
	tone: 'regular' | 'admin'
	onNavigate?: () => void
}) {
	const Icon = item.icon

	// In the menu variant the tile class goes on DropdownMenuItem (it tailwind-merges against the item's own
	// px/py/rounded/text base); passing it to the Link instead would leave both sets in the class string.
	const link = (
		<Link
			href={item.href}
			onClick={onNavigate}
			className={variant === 'menu' ? undefined : cn(TILE_CLASS, 'focus-visible:bg-accent focus-visible:outline-none')}
		>
			{/* size-6, not h-6 w-6: DropdownMenuItem force-sizes any svg whose class lacks "size-" down to size-4. */}
			<Icon
				className={cn(
					'size-6',
					tone === 'admin' ? 'text-red-600 dark:text-red-400' : 'text-blue-600 dark:text-blue-400'
				)}
			/>
			<span className="text-[11px] leading-tight font-medium">{label}</span>
		</Link>
	)

	// Menu tiles must be real menu items: Radix blocks Tab inside menu content, so a plain link would be
	// unreachable by keyboard, and only an item's select closes the dropdown on click.
	if (variant === 'menu') {
		return (
			<DropdownMenuItem asChild className={TILE_CLASS}>
				{link}
			</DropdownMenuItem>
		)
	}

	return link
}

export function AppGrid({
	onNavigate,
	variant = 'plain',
	showCli = false,
}: {
	onNavigate?: () => void
	variant?: AppGridVariant
	showCli?: boolean
}) {
	const t = useTranslations('nav.apps')
	const tSidebar = useTranslations('dashboard.sidebar')
	const { data: session } = useSession()
	const isAdmin = session?.user?.isAdmin ?? false
	const regularItems = showCli ? [...APP_ITEMS, ...CLI_ITEMS] : APP_ITEMS

	const renderTile = (item: AppItem, tone: 'regular' | 'admin') => (
		<AppTile
			key={item.key}
			item={item}
			label={tSidebar(item.key)}
			variant={variant}
			tone={tone}
			onNavigate={onNavigate}
		/>
	)

	return (
		<>
			{/* role="group" keeps the menu variant valid: role="menu" only allows menuitem/group/separator children. */}
			<div role="group" className="grid grid-cols-3 gap-2 p-1">
				{regularItems.map((item) => renderTile(item, 'regular'))}
			</div>

			{isAdmin && (
				<>
					<p className="px-3 pt-3 pb-1 text-xs font-semibold tracking-wider text-muted-foreground uppercase">
						{t('adminSection')}
					</p>
					<div role="group" className="grid grid-cols-3 gap-2 p-1">
						{ADMIN_ITEMS.map((item) => renderTile(item, 'admin'))}
					</div>
				</>
			)}
		</>
	)
}
